import { useDisclosure } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { ExecutionTypeEnum } from "@/__generated__/graphql";
import { ActionItem, Actions } from "@/components/Actions";
import DeleteApplication from "@/components/DeleteModals/DeleteApplication";
import { CopyIcon, DeleteIcon } from "@/components/shared/ReleaseIcons";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { useWorkspace } from "@/providers/useWorkspace";

const ApplicationActions_Application = graphql(`
  fragment ApplicationActions_Application on App {
    id
    executionType
    ...DeleteApplication_Application
  }
`);

export function ApplicationActions(props: {
  application?: FragmentType<typeof ApplicationActions_Application>;
  hideCreateEnvironment?: boolean;
}) {
  const { isSandbox } = useWorkspace();
  const application = getFragmentData(ApplicationActions_Application, props.application);
  const executionType = application?.executionType;
  const { workspaceId, applicationId } = useReadySearchParams();
  const disclosureProps = useDisclosure();
  const { onOpen } = disclosureProps;
  const { hideCreateEnvironment = false } = props;

  if (!application) return null;

  const primaryAction: ActionItem =
    executionType === ExecutionTypeEnum.Runnable
      ? {
          type: "link",
          label: "Run Job",
          href: {
            pathname: "/workspaces/[workspaceId]/applications/[applicationId]/jobs/create",
            query: {
              workspaceId,
              applicationId,
            },
          },
        }
      : {
          type: "link",
          label: "Create Environment",
          href: {
            pathname: isSandbox
              ? "/workspaces/[workspaceId]/applications/[applicationId]/environments/wizard"
              : "/workspaces/[workspaceId]/applications/[applicationId]/environments/create",
            query: {
              workspaceId,
              applicationId,
            },
          },
        };

  const actions: ActionItem[] = [
    {
      type: "menu",
      label: "Actions",
      items: [
        {
          type: "link",
          label: "Clone",
          icon: CopyIcon,
          href: {
            pathname: "/workspaces/[workspaceId]/applications/[applicationId]/clone",
            query: {
              workspaceId,
              applicationId,
            },
          },
        },
        {
          type: "function",
          label: "Delete",
          icon: DeleteIcon,
          function: onOpen,
        },
      ],
    },
    ...(!hideCreateEnvironment || executionType === ExecutionTypeEnum.Runnable ? [primaryAction] : []),
  ];

  return (
    <>
      <DeleteApplication application={application} {...disclosureProps} />
      <Actions actions={actions} />
    </>
  );
}
